import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, Input, Textarea, Toggle, Modal, Confirm, Empty } from "@/pages/admin/managers/ui";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function PackagesManager() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => setItems((await api.get("/admin/packages")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.name?.trim()) return toast.error("El nombre es obligatorio.");
    try {
      const body = {
        name: modal.name, price: modal.price || "Consultar precio", duration: modal.duration || "",
        edited_photos: modal.edited_photos || "", outfit_changes: modal.outfit_changes || "",
        location: modal.location || "", delivery_time: modal.delivery_time || "",
        deposit_required: modal.deposit_required || "", cancellation_policy: modal.cancellation_policy || "",
        includes: (modal.includesText || "").split("\n").map((s) => s.trim()).filter(Boolean),
        is_visible: modal.is_visible !== false,
      };
      if (modal.id) await api.put(`/admin/packages/${modal.id}`, body);
      else await api.post("/admin/packages", body);
      toast.success("Paquete guardado.");
      setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => { await api.delete(`/admin/packages/${confirmId}`); toast.success("Paquete eliminado."); setConfirmId(null); load(); };

  const openEdit = (p) => setModal({ ...p, includesText: (p.includes || []).join("\n") });

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Btn data-testid="add-package-btn" onClick={() => setModal({ is_visible: true, price: "Consultar precio" })}><Plus size={16} className="inline mr-1" /> Nuevo paquete</Btn>
      </div>
      {items.length === 0 ? <Empty>No hay paquetes.</Empty> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`package-row-${p.id}`} className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
              <p className="font-serif text-2xl text-white">{p.name} {!p.is_visible && <span className="text-xs text-[#8C8375]">(oculto)</span>}</p>
              <p className="text-[#C5A059] font-mono mt-1">{p.price}</p>
              <ul className="mt-3 text-xs text-[#A1A1AA] space-y-1">
                {p.duration && <li>· {p.duration}</li>}
                {p.edited_photos && <li>· {p.edited_photos}</li>}
                {(p.includes || []).slice(0, 3).map((i, idx) => <li key={idx}>· {i}</li>)}
              </ul>
              <div className="flex gap-2 mt-4">
                <Btn variant="subtle" data-testid={`package-edit-${p.id}`} onClick={() => openEdit(p)}><Pencil size={14} /></Btn>
                <Btn variant="danger" data-testid={`package-delete-${p.id}`} onClick={() => setConfirmId(p.id)}><Trash2 size={14} /></Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.id ? "Editar paquete" : "Nuevo paquete"} testid="package-modal">
        {modal && (
          <>
            <Field label="Nombre"><Input data-testid="package-name-input" value={modal.name || ""} onChange={(e) => setModal({ ...modal, name: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Precio"><Input value={modal.price || ""} onChange={(e) => setModal({ ...modal, price: e.target.value })} placeholder="Consultar precio" /></Field>
              <Field label="Duración"><Input value={modal.duration || ""} onChange={(e) => setModal({ ...modal, duration: e.target.value })} /></Field>
              <Field label="Fotos editadas"><Input value={modal.edited_photos || ""} onChange={(e) => setModal({ ...modal, edited_photos: e.target.value })} /></Field>
              <Field label="Cambios de ropa"><Input value={modal.outfit_changes || ""} onChange={(e) => setModal({ ...modal, outfit_changes: e.target.value })} /></Field>
              <Field label="Locación"><Input value={modal.location || ""} onChange={(e) => setModal({ ...modal, location: e.target.value })} /></Field>
              <Field label="Tiempo de entrega"><Input value={modal.delivery_time || ""} onChange={(e) => setModal({ ...modal, delivery_time: e.target.value })} /></Field>
              <Field label="Depósito requerido"><Input value={modal.deposit_required || ""} onChange={(e) => setModal({ ...modal, deposit_required: e.target.value })} /></Field>
            </div>
            <Field label="Servicios incluidos (uno por línea)"><Textarea rows={3} value={modal.includesText || ""} onChange={(e) => setModal({ ...modal, includesText: e.target.value })} /></Field>
            <Field label="Política de cancelación"><Textarea rows={2} value={modal.cancellation_policy || ""} onChange={(e) => setModal({ ...modal, cancellation_policy: e.target.value })} /></Field>
            <Toggle testid="package-visible-toggle" checked={modal.is_visible !== false} onChange={(v) => setModal({ ...modal, is_visible: v })} label="Visible en el sitio" />
            <div className="flex justify-end gap-3 pt-2">
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
              <Btn data-testid="package-save-btn" onClick={save}>Guardar</Btn>
            </div>
          </>
        )}
      </Modal>
      <Confirm open={!!confirmId} onClose={() => setConfirmId(null)} onConfirm={del} message="¿Eliminar este paquete?" />
    </div>
  );
}
